/* =========================================================================
   KPI Sports — Club Shops landing page
   Vanilla JS — 7-question quiz, shop-format recommendation, illustrative
   preview (desktop + phone mock), enquiry handoff to the native Shopify
   contact form.

   The preview's products are illustrative placeholders generated entirely
   here client-side (name/price/a real stock photo via ITEM_IMAGES, else a
   colour-tinted tile), not pulled from a live Shopify collection. This
   store's Locksmith app rewrites theme files on save and auto-wraps any
   `for product in collection.products` loop with a visibility check; when
   this page used a real Liquid product loop, Locksmith's injected check
   silently dropped every product. Keep product data client-side unless
   that's resolved on the Locksmith side.
   ========================================================================= */
(function () {
  'use strict';

  var $ = function (sel, ctx) { return (ctx || document).querySelector(sel); };
  var $$ = function (sel, ctx) { return Array.prototype.slice.call((ctx || document).querySelectorAll(sel)); };

  var root = $('[data-cse]');
  if (!root) return;

  var configEl = $('[data-cse-config]', root);
  var config = configEl ? JSON.parse(configEl.textContent) : {};

  /* Fires into Shopify's own analytics (Web Pixels / customer events) so
     this doesn't depend on any third-party tag (GA4, Meta Pixel etc.)
     being installed. Safe no-op wherever Shopify.analytics isn't present
     (e.g. a duplicated/offline theme preview). */
  var quizStartTracked = false;
  function trackEvent(name, data) {
    try {
      if (window.Shopify && window.Shopify.analytics && typeof window.Shopify.analytics.publish === 'function') {
        window.Shopify.analytics.publish(name, data || {});
      }
    } catch (e) { /* analytics is best-effort — never block the quiz on it */ }
  }
  function trackQuizStart() {
    if (quizStartTracked) return;
    quizStartTracked = true;
    trackEvent('club_shop_quiz_started', {});
  }

  var QUESTIONS = [
    {
      id: 'audience', kind: 'single', cols: 3,
      title: "Who's the shop for?",
      sub: 'Pick your sport if you have one. Otherwise pick your organisation type.',
      options: [
        { v: 'football', l: 'Football (Soccer)' }, { v: 'netball', l: 'Netball' },
        { v: 'hockey', l: 'Hockey' }, { v: 'athletics', l: 'Athletics' },
        { v: 'rugby', l: 'Rugby League / Union' }, { v: 'afl', l: 'AFL / AFLW' },
        { v: 'basketball', l: 'Basketball' }, { v: 'cricket', l: 'Cricket' },
        { v: 'touch', l: 'Touch / Oztag' }, { v: 'school', l: 'School' },
        { v: 'assoc', l: 'Association' }, { v: 'other', l: 'Business / Other' }
      ]
    },
    {
      id: 'access', kind: 'single', cols: 1,
      title: 'Who gets to see the shop?',
      sub: 'Your shop can be open to everyone, or show each person only what applies to them.',
      options: [
        { v: 'open', l: 'Anyone can order anything in the list', d: 'One shop, one product list, open to members, parents and supporters alike.' },
        { v: 'roles', l: 'Products pre-filtered for teams, admin and supporters', d: 'People answer a few short questions, then only see the items they are meant to order.' },
        { v: 'unsure', l: 'Not sure yet', d: "We'll show you both and recommend the one that fits how you order." }
      ]
    },
    {
      id: 'items', kind: 'single', cols: 1,
      title: 'Custom-made or off the shelf?',
      sub: 'This is the biggest driver of how your shop runs and how quickly it can go live.',
      options: [
        { v: 'shelf', l: 'Off the shelf with our logo', d: 'Stock garments with your branding or logo. 2–4 week turnaround and reliable resupply.' },
        { v: 'custom', l: 'Fully custom, designed for us', d: 'Custom designed apparel, made to order for your club, school or business. 4–6 week turnaround.' },
        { v: 'both', l: 'A mix of both', d: 'Custom feature pieces alongside stock items people can buy any time.' }
      ]
    },
    {
      id: 'personal', kind: 'multi', cols: 2,
      title: 'Are you looking to personalise items?',
      sub: 'Pick everything people should be able to enter themselves when they order.',
      options: [
        { v: 'name', l: 'Name on the item' }, { v: 'number', l: 'Number' },
        { v: 'initials', l: 'Initials' }, { v: 'team', l: 'Team, class or department' },
        { v: 'none', l: 'No personalisation needed' }
      ]
    },
    {
      id: 'runmode', kind: 'single', cols: 2,
      title: 'How should the shop run?',
      sub: 'Ordering rounds keep everyone on one production run. An always-open shop lets people order whenever they need.',
      options: [
        { v: 'round', l: 'Ordering window', d: 'Opens and closes on a set date, then everything is made and delivered together.' },
        { v: 'open', l: 'Always open', d: 'A permanent shop. New members, staff or supporters can order any time of year.' },
        { v: 'unsure', l: 'Not sure yet', d: "Tell us your season or intake dates and we'll recommend the pattern." }
      ]
    },
    {
      id: 'delivery', kind: 'single', cols: 2,
      title: 'How should the gear get to everyone?',
      sub: 'This decides whether we pick and pack per person or send one consolidated delivery.',
      options: [
        { v: 'individual', l: 'Ship to each person', d: 'Every order goes direct to the buyer. Nobody has to hand out boxes.' },
        { v: 'bulk', l: 'One bulk delivery to us', d: 'Everything arrives together, sorted and labelled per person for handout.' },
        { v: 'mix', l: 'A mix of both', d: 'Bulk for the main kit, direct shipping for anything ordered later.' },
        { v: 'unsure', l: 'Not sure yet', d: "We'll suggest the approach that fits your numbers." }
      ]
    },
    { id: 'identity', kind: 'identity', title: 'Tell us about you', sub: 'Your name, logo, colours and rough numbers drive the shop preview below.' }
  ];

  var HEADCOUNTS = ['Under 20', '20–49', '50–99', '100 or more'];

  var PURPOSES = [
    'General merch shop for members and supporters',
    'Team or class kit ordering',
    'Season pre-order round for a club',
    'Staff, corporate or school uniforms',
    'Event, tour or one-off campaign merch'
  ];

  var AUD = {
    school: { noun: 'uniforms', who: 'student', chips: ['New student', 'Returning student', 'Staff'], req: 'Every student needs these items for the year.', kit: 'House Shirt', items: ['Sport Shirt', 'Sport Shorts', 'Socks', 'Polo', 'Spray Jacket', 'Cap'] },
    assoc: { noun: 'kit', who: 'member', chips: ['Player', 'Official', 'Supporter'], req: 'Every registered member needs these to take the field this season.', kit: 'Representative Jersey', items: ['Playing Jersey', 'Playing Shorts', 'Playing Socks', 'Polo', 'Spray Jacket', 'Cap'] },
    other: { noun: 'uniforms', who: 'team member', chips: ['New starter', 'Team member', 'Admin'], req: 'The core uniform every team member needs.', kit: 'Branded Polo', items: ['Branded Polo', 'Work Shorts', 'Softshell Jacket', 'Cap'] }
  };
  var AUD_DEFAULT = { noun: 'playing kit', who: 'player', chips: ['New player', 'Returning player', 'Staff'], req: 'Every new player needs these to take the field this season.', kit: '', items: ['Playing Jersey', 'Playing Shorts', 'Playing Socks', 'Player Polo', 'Spray Jacket', 'Cap'] };

  var KIT = {
    football: 'Home Playing Jersey', rugby: 'Home Playing Jersey',
    afl: 'Home Guernsey', netball: 'Playing Dress', basketball: 'Reversible Singlet',
    cricket: 'Playing Shirt', touch: 'Playing Jersey', hockey: 'Playing Jersey',
    athletics: 'Athletic Singlet', school: 'House Shirt', assoc: 'Representative Jersey', other: 'Branded Polo'
  };

  /* Real generic/stock product photos, keyed by the exact garment name they
     illustrate (KPI-supplied). Deliberately not club-coloured: these are
     stock photos of a real garment, not a live render of the finished
     product, so a small colour tag over the photo (set in fillProductGrid)
     stands in for "this is what it'll look like in your colours" instead
     of trying to recolour the photo itself. */
  var ITEM_IMAGES = {
    'Playing Jersey': 'https://cdn.shopify.com/s/files/1/0413/1543/4647/files/TacticJersey-Black.jpg?v=1747892008',
    'Guernsey': 'https://cdn.shopify.com/s/files/1/0413/1543/4647/files/TacticJersey-Black.jpg?v=1747892008',
    'Playing Dress': 'https://cdn.shopify.com/s/files/1/0413/1543/4647/files/ND3228229.jpg?v=1750899533',
    'Reversible Singlet': 'https://cdn.shopify.com/s/files/1/0413/1543/4647/files/BasketballJersey8BJ8Back.jpg?v=1751947438',
    'Playing Shirt': 'https://cdn.shopify.com/s/files/1/0413/1543/4647/files/CustomCricket-LSP3.jpg?v=1756349783',
    'House Shirt': 'https://cdn.shopify.com/s/files/1/0413/1543/4647/files/CustomPolosCWPS18Mens.jpg?v=1756348374',
    'Representative Jersey': 'https://cdn.shopify.com/s/files/1/0413/1543/4647/files/TacticJersey-Black.jpg?v=1747892008',
    'Branded Polo': 'https://cdn.shopify.com/s/files/1/0413/1543/4647/files/CustomPolosCWPS18Mens.jpg?v=1756348374',
    'Playing Shorts': 'https://cdn.shopify.com/s/files/1/0413/1543/4647/files/UltraShort-Black-Front.jpg?v=1748395605',
    'Playing Socks': 'https://cdn.shopify.com/s/files/1/0413/1543/4647/products/Sock16.png?v=1649306265',
    'Player Polo': 'https://cdn.shopify.com/s/files/1/0413/1543/4647/files/Icon_Polo.jpg?v=1777521731',
    'Spray Jacket': 'https://cdn.shopify.com/s/files/1/0413/1543/4647/products/SprayJacketDriJacket_Navy.png?v=1645855868',
    'Cap': 'https://cdn.shopify.com/s/files/1/0413/1543/4647/products/CapBlack_002.png?v=1632106707',
    'Sport Shorts': 'https://cdn.shopify.com/s/files/1/0413/1543/4647/files/UltraShort-Black-Front.jpg?v=1748395605',
    'Socks': 'https://cdn.shopify.com/s/files/1/0413/1543/4647/products/Sock16.png?v=1649306265',
    'Polo': 'https://cdn.shopify.com/s/files/1/0413/1543/4647/files/KPIIconPolo-Black-Front.jpg?v=1749436834',
    'Work Shorts': 'https://cdn.shopify.com/s/files/1/0413/1543/4647/files/UltraShort-Black-Front.jpg?v=1748395605',
    'Softshell Jacket': 'https://cdn.shopify.com/s/files/1/0413/1543/4647/files/PremierJacket-Black-Front.jpg?v=1707950541',
    'Supporter Cap': 'https://cdn.shopify.com/s/files/1/0413/1543/4647/products/CapBlack_002.png?v=1632106707',
    'Away Jersey': 'https://cdn.shopify.com/s/files/1/0413/1543/4647/files/TacticJersey-WhiteandBlack-Front.jpg?v=1747892546'
  };

  /* Heroes that are an actual team jersey — these get an Away Jersey tile
     alongside the hero. Cricket, netball, basketball, school and business
     heroes don't have a "home/away" concept, so they're left out. */
  var JERSEY_HEROES = ['Playing Jersey', 'Guernsey', 'Representative Jersey'];

  var LOGO_CARRIER_VARIANT_ID = 50526681563384;

  var state = {
    step: 0,
    done: false,
    a: { audience: null, org: '', logo: '', logoUrl: '', logoUploadFailed: false, purpose: '', headcount: '', primary: '#DB124B', secondary: '#002E65', access: null, items: null, personal: [], runmode: null, delivery: null },
    c: { role: '', notes: '' }
  };
  var logoUploadPromise = null;

  function readable(hex) {
    var h = (hex || '').replace('#', '');
    if (h.length !== 6) return '#FFFFFF';
    var l = (parseInt(h.slice(0, 2), 16) * 299 + parseInt(h.slice(2, 4), 16) * 587 + parseInt(h.slice(4, 6), 16) * 114) / 1000;
    return l > 150 ? '#1F1F1F' : '#FFFFFF';
  }

  function shade(hex, pct) {
    var h = (hex || '').replace('#', '');
    if (h.length !== 6) return hex;
    var num = parseInt(h, 16), r = (num >> 16) & 0xFF, g = (num >> 8) & 0xFF, b = num & 0xFF;
    var t = pct < 0 ? 0 : 255, p = Math.abs(pct);
    r = Math.round((t - r) * p) + r; g = Math.round((t - g) * p) + g; b = Math.round((t - b) * p) + b;
    return '#' + [r, g, b].map(function (v) { return ('0' + v.toString(16)).slice(-2); }).join('');
  }

  function slugify(s) {
    return (s.trim() || 'your-club').toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
  }

  function initialsOf(s) {
    var club = s.trim() || 'Your Club';
    return club.split(/\s+/).map(function (w) { return w[0]; }).join('').slice(0, 3).toUpperCase();
  }

  function answered(q) {
    var a = state.a;
    if (q.kind === 'identity') return a.org.trim().length > 1 && !!a.purpose && !!a.headcount;
    if (q.kind === 'multi') return a.personal.length > 0;
    return !!a[q.id];
  }

  function shopType() {
    var a = state.a;
    if (a.items === 'both') return 'hybrid';
    if (a.items === 'custom') return a.runmode === 'open' ? 'hybrid' : 'preorder';
    if (a.items === 'shelf') return a.runmode === 'round' ? 'hybrid' : 'retail';
    return 'retail';
  }

  function recommendation() {
    var a = state.a, club = a.org.trim() || 'your organisation', t = shopType();
    if (t === 'preorder') return {
      key: t, name: 'Pre-Order Shop', tagline: 'Pre-order window // Products are made and handled // Delivery sorted',
      headline: 'Run one ordering window or more throughout the year, then make exactly what was ordered.',
      summary: 'Because your items are made to order, the shop runs as an ordering window: it opens on a set date, ' + club + ' shares one link, and when it closes we produce precisely what was ordered. No stock risk, no leftover sizes, and every order already has its sizes and personalisation attached.',
      timeline: 'Shop live in 1–2 weeks // production begins the day the ordering window closes // 4–6 weeks to delivery'
    };
    if (t === 'retail') return {
      key: t, name: 'Retail Merch Shop', tagline: 'In stock // ships now',
      headline: 'Keep a branded shop open all year and ship as orders come in.',
      summary: 'Stock garments branded for ' + club + ', held ready and dispatched order by order. The shop never closes, new members and staff can buy whenever they join, and nobody waits on a production run.',
      timeline: 'Shop live in 1–2 weeks // Orders ship in 5–10 business days once placed // Set and forget'
    };
    return {
      key: t, name: 'Hybrid Shop', tagline: 'Pre-order kit & retail merch // Separate order timelines // one shop',
      headline: 'Stock items are always available. Custom items open when needed.',
      summary: 'Your feature items run as an ordering window, while stock merch sits ready and ships straight away. ' + club + ' gets properly made kit without making everyone wait weeks for a t-shirt — all inside one shop and one checkout.',
      timeline: 'Shop live in 1–2 weeks // Stock merch ships in 5–10 business days // Pre-order items 4–6 weeks after ordering closes'
    };
  }

  function reasons() {
    var a = state.a, out = [];
    if (a.items === 'custom') out.push('You want custom-designed apparel, which is made to order — so ordering has to close before production starts.');
    if (a.items === 'shelf') out.push('Off-the-shelf garments with your branding means no production window and easy resupply all year.');
    if (a.items === 'both') out.push('You want custom and stock items together, which is exactly what a hybrid shop is built to separate.');
    if (a.runmode === 'round') out.push('A single ordering window keeps everyone on one production run and one delivery date.');
    if (a.runmode === 'open') out.push('An always-open shop means new members, staff and supporters can order at any time of year.');
    if (a.access === 'roles') out.push('Team, class or role questions filter the shop so each person only sees the items they need.');
    if (a.access === 'open') out.push('One open product list — no questionnaire in front of the shop.');
    var pOpts = (QUESTIONS.filter(function (q) { return q.id === 'personal'; })[0] || {}).options || [];
    var p = a.personal.filter(function (x) { return x !== 'none'; }).map(function (v) {
      var o = pOpts.filter(function (o) { return o.v === v; })[0];
      return o ? o.l.toLowerCase() : v;
    });
    if (p.length) out.push('Personalisation captured at checkout (' + p.join(', ') + ') and held on a live print record per order.');
    if (a.delivery === 'bulk') out.push('One consolidated delivery, sorted and labelled per person so handout takes minutes.');
    if (a.delivery === 'individual') out.push('Every order ships direct to the buyer, so nobody has to distribute boxes.');
    if (a.delivery === 'mix') out.push('Bulk delivery for the main order, direct shipping for anything ordered after that.');
    if (a.purpose) out.push('Built around your goal: ' + a.purpose.toLowerCase() + '.');
    return out.slice(0, 4).map(function (text, i) { return { text: text, n: String(i + 1).padStart(2, '0') }; });
  }

  function previewProducts() {
    var a = state.a, t = shopType();
    var aud = AUD[a.audience] || AUD_DEFAULT;
    /* aud.items[0] duplicates the hero/kit name, so the extra tiles start
       at index 1. Built from however many extras exist rather than fixed
       indices, since audiences don't all offer the same number (e.g.
       'other' only has generic images for 3 extras, not the full 5). */
    var extra = (aud.items || []).slice(1);
    var abbr = (a.org.trim().split(/\s+/).map(function (w) { return w[0]; }).join('') || 'CLUB').toUpperCase().slice(0, 4);
    var hero = (a.audience && AUD[a.audience]) ? aud.kit : (KIT[a.audience] || 'Home Playing Jersey').replace('Home ', '');
    var stock = function (isKit) { return t === 'retail' ? 'IN STOCK' : t === 'preorder' ? 'PRE-ORDER' : (isKit ? 'PRE-ORDER' : 'IN STOCK'); };
    var p = a.personal.filter(function (x) { return x !== 'none'; });
    var pers = p.length > 0;
    var colorCycle = ['i', 's', 'p'];
    /* Sample preview only: every price shows $0 so nothing reads as a quote. */
    var PREVIEW_PRICE = '$0.00';
    var reqPrices = [PREVIEW_PRICE];
    var optPrices = [PREVIEW_PRICE];
    var reqExtra = extra.slice(0, 2);
    var optExtra = extra.slice(2);

    var list = [
      { name: abbr + ' ' + hero, raw: hero, price: PREVIEW_PRICE, tag: stock(true), g: 'req', c: 'p', pers: pers }
    ];
    /* Home/away only makes sense for an actual team jersey — not a cricket
       shirt, netball dress, basketball singlet, school shirt or polo. */
    if (JERSEY_HEROES.indexOf(hero) !== -1) {
      list.push({ name: abbr + ' Away Jersey', raw: 'Away Jersey', price: PREVIEW_PRICE, tag: stock(true), g: 'req', c: 's', pers: false });
    }
    reqExtra.forEach(function (name, i) {
      list.push({ name: abbr + ' ' + name, raw: name, price: reqPrices[i % reqPrices.length], tag: stock(true), g: 'req', c: colorCycle[i % colorCycle.length], pers: false });
    });
    optExtra.forEach(function (name, i) {
      var isLast = i === optExtra.length - 1;
      list.push({ name: abbr + ' ' + name, raw: name, price: optPrices[i % optPrices.length], tag: isLast ? 'IN STOCK' : stock(false), g: 'opt', c: colorCycle[i % colorCycle.length], pers: isLast ? false : pers });
    });
    if ((a.purpose || '').indexOf('merch') > -1) list.push({ name: abbr + ' Supporter Cap', raw: 'Supporter Cap', price: PREVIEW_PRICE, tag: 'IN STOCK', g: 'opt', c: 'p', pers: false });

    var personalLabel = a.personal.indexOf('name') !== -1 ? 'Name on item'
      : a.personal.indexOf('initials') !== -1 ? 'Initials'
      : a.personal.indexOf('number') !== -1 ? 'Number' : 'Team, class or department';

    return list.map(function (item) {
      return {
        title: item.name, image: ITEM_IMAGES[item.raw] || '', price: item.price, tag: item.tag, group: item.g, colorKey: item.c,
        personalised: !!item.pers, personalLabel: personalLabel
      };
    });
  }

  /* ===================== DOM refs ===================== */
  var quizPanel = $('[data-cse-quiz]', root);
  var resultPanel = $('[data-cse-result]', root);
  var progressBar = $('[data-cse-progress]', root);
  var stepLabel = $('[data-cse-step-label]', root);
  var kindLabel = $('[data-cse-kind-label]', root);
  var quizTitle = $('[data-cse-title]', root);
  var quizSub = $('[data-cse-sub]', root);
  var backBtn = $('[data-cse-back]', root);
  var nextBtn = $('[data-cse-next]', root);
  var helperText = $('[data-cse-helper]', root);
  var stepPanels = $$('[data-cse-step-panel]', root);

  var orgInput = $('[data-cse-org]', root);
  var purposeSelect = $('[data-cse-purpose]', root);
  var headcountSelect = $('[data-cse-headcount]', root);
  var logoInput = $('[data-cse-logo-input]', root);
  var logoPreviewWrap = $('[data-cse-logo-preview-wrap]', root);
  var logoRemoveBtn = $('[data-cse-logo-remove]', root);
  var primarySwatchRow = $('[data-cse-primary-swatches]', root);
  var secondarySwatchRow = $('[data-cse-secondary-swatches]', root);
  var primaryCustom = $('[data-cse-primary-custom]', root);
  var secondaryCustom = $('[data-cse-secondary-custom]', root);

  if (purposeSelect) {
    purposeSelect.innerHTML = '<option value="">Select the closest fit…</option>' +
      PURPOSES.map(function (p) { return '<option value="' + escapeAttr(p) + '">' + escapeHtml(p) + '</option>'; }).join('');
    purposeSelect.addEventListener('change', function () {
      state.a.purpose = purposeSelect.value;
      refreshFooter();
    });
  }
  if (headcountSelect && headcountSelect.tagName === 'SELECT') {
    headcountSelect.innerHTML = '<option value="">Select a rough range…</option>' +
      HEADCOUNTS.map(function (p) { return '<option value="' + escapeAttr(p) + '">' + escapeHtml(p) + '</option>'; }).join('');
    headcountSelect.addEventListener('change', function () {
      state.a.headcount = headcountSelect.value;
      refreshFooter();
    });
  } else if (headcountSelect) {
    headcountSelect.addEventListener('input', function () {
      var digits = headcountSelect.value.replace(/[^0-9]/g, '').replace(/^0+/, '');
      if (headcountSelect.value !== digits) headcountSelect.value = digits;
      state.a.headcount = digits;
      refreshFooter();
    });
  }

  var SWATCHES = [
    { name: 'KPI Red', hex: '#DB124B' },
    { name: 'KPI Royal', hex: '#053B84' },
    { name: 'KPI Yellow', hex: '#FFF200' },
    { name: 'KPI Navy', hex: '#002E65' },
    { name: 'KPI Orange', hex: '#F15A25' },
    { name: 'KPI Pink', hex: '#F49AC1' },
    { name: 'KPI Purple', hex: '#84607F' },
    { name: 'KPI Black', hex: '#231F20' },
    { name: 'KPI White', hex: '#FFFFFF' },
    { name: 'KPI Bottle Green', hex: '#00452B' },
    { name: 'KPI Mid Blue', hex: '#005D98' }
  ];

  function swatchName(hex) {
    var h = (hex || '').toUpperCase();
    var m = SWATCHES.filter(function (s) { return s.hex === h; })[0];
    return m ? m.name : null;
  }
  function colourLabel(hex) {
    var h = (hex || '').toUpperCase();
    var name = swatchName(h);
    return name ? (name + ' (' + h + ')') : h;
  }

  function buildSwatches(container, key) {
    container.innerHTML = '';
    SWATCHES.forEach(function (s) {
      var b = document.createElement('button');
      b.type = 'button';
      b.className = 'cse-swatch';
      b.title = s.name;
      b.style.background = s.hex;
      b.addEventListener('click', function () {
        state.a[key] = s.hex;
        renderIdentitySwatches();
      });
      container.appendChild(b);
    });
  }
  if (primarySwatchRow) buildSwatches(primarySwatchRow, 'primary');
  if (secondarySwatchRow) buildSwatches(secondarySwatchRow, 'secondary');

  function renderIdentitySwatches() {
    if (primarySwatchRow) $$('.cse-swatch', primarySwatchRow).forEach(function (b) { b.classList.toggle('is-active', b.style.background === hexToRgbLoose(state.a.primary)); });
    if (secondarySwatchRow) $$('.cse-swatch', secondarySwatchRow).forEach(function (b) { b.classList.toggle('is-active', b.style.background === hexToRgbLoose(state.a.secondary)); });
    if (primaryCustom) primaryCustom.value = state.a.primary;
    if (secondaryCustom) secondaryCustom.value = state.a.secondary;
  }
  function hexToRgbLoose(hex) {
    var d = document.createElement('div');
    d.style.background = hex;
    return d.style.background;
  }

  if (orgInput) orgInput.addEventListener('input', function () { state.a.org = orgInput.value; refreshFooter(); });
  if (primaryCustom) primaryCustom.addEventListener('input', function () { state.a.primary = primaryCustom.value; renderIdentitySwatches(); });
  if (secondaryCustom) secondaryCustom.addEventListener('input', function () { state.a.secondary = secondaryCustom.value; renderIdentitySwatches(); });

  /* The logo needs a real, permanent link the team can click from the
     enquiry email — a local blob: URL only exists in this tab. Shopify's
     contact form can't carry file attachments, so the file rides along as
     a cart line-item property on a $0 utility variant (same trick as
     assets/summer-competition.js): fetch() posts multipart FormData to
     /cart/add, Shopify uploads the file to its CDN and writes the property
     value as a URL containing "/uploads/", then the line is removed again
     so nothing lingers in the visitor's real cart. */
  function cartJson() {
    return fetch('/cart.js', { credentials: 'same-origin' }).then(function (r) { return r.json(); });
  }
  function removeCartLine(key) {
    return fetch('/cart/change.js', {
      method: 'POST',
      headers: { 'Content-Type': 'application/json', Accept: 'application/json' },
      body: JSON.stringify({ id: key, quantity: 0 }),
      credentials: 'same-origin'
    });
  }
  function purgeLogoCarrierLines() {
    return cartJson().then(function (cart) {
      var ours = (cart.items || []).filter(function (it) { return String(it.id) === String(LOGO_CARRIER_VARIANT_ID); });
      return ours.reduce(function (p, it) { return p.then(function () { return removeCartLine(it.key); }); }, Promise.resolve());
    });
  }
  function uploadLogoFile(file) {
    return purgeLogoCarrierLines().then(function () {
      var fd = new FormData();
      fd.append('id', String(LOGO_CARRIER_VARIANT_ID));
      fd.append('quantity', '1');
      fd.append('properties[Club Logo]', file, file.name);
      return fetch('/cart/add', { method: 'POST', body: fd, credentials: 'same-origin' });
    }).then(cartJson).then(function (cart) {
      var item = (cart.items || []).filter(function (it) { return String(it.id) === String(LOGO_CARRIER_VARIANT_ID); }).pop();
      var url = item && item.properties ? String(item.properties['Club Logo'] || '') : '';
      var cleanup = item ? removeCartLine(item.key) : Promise.resolve();
      return cleanup.then(function () {
        if (url.indexOf('/uploads/') === -1) throw new Error('logo-upload-failed');
        return url;
      });
    });
  }
  /* uploadLogoFile's own .catch (below) already resolves logoUploadPromise
     cleanly on failure — it never rejects — so this just needs to wait for
     whichever outcome (success or logged failure) has already landed. */
  function ensureLogoUrl() {
    if (!state.a.logo || state.a.logoUrl || !logoUploadPromise) return Promise.resolve();
    return logoUploadPromise;
  }

  if (logoInput) logoInput.addEventListener('change', function () {
    var f = logoInput.files && logoInput.files[0];
    if (!f) return;
    state.a.logo = URL.createObjectURL(f);
    state.a.logoUrl = '';
    renderLogoPreview();
    state.a.logoUploadFailed = false;
    logoUploadPromise = uploadLogoFile(f).then(function (url) {
      state.a.logoUrl = url;
    }).catch(function (err) {
      state.a.logoUrl = '';
      state.a.logoUploadFailed = true;
      /* Never block the enquiry on this — a missing logo link is
         recoverable (chase the club for the file), a lost enquiry isn't.
         Logged so a repeat of the Sept 2026 "carrier product got
         unpublished" incident shows up in the console instead of just
         looking like the visitor never uploaded anything. */
      if (window.console && console.error) console.error('Club Shop Enquiry: logo upload failed', err);
    });
  });
  if (logoRemoveBtn) logoRemoveBtn.addEventListener('click', function () {
    state.a.logo = '';
    state.a.logoUrl = '';
    logoUploadPromise = null;
    purgeLogoCarrierLines();
    if (logoInput) logoInput.value = '';
    renderLogoPreview();
  });
  function renderLogoPreview() {
    if (!logoPreviewWrap) return;
    logoPreviewWrap.innerHTML = '';
    if (!state.a.logo) { if (logoRemoveBtn) logoRemoveBtn.hidden = true; return; }
    var div = document.createElement('div');
    div.className = 'cse-logo-preview';
    div.style.backgroundImage = 'url(' + state.a.logo + ')';
    logoPreviewWrap.appendChild(div);
    if (logoRemoveBtn) logoRemoveBtn.hidden = false;
  }

  /* ===================== Step rendering ===================== */
  function renderOptions(q, panel) {
    var wrap = $('.cse-options', panel);
    if (!wrap) return;
    wrap.innerHTML = '';
    wrap.style.gridTemplateColumns = 'repeat(auto-fit, minmax(min(100%, ' + (q.cols === 1 ? '420px' : q.cols === 2 ? '280px' : '190px') + '), 1fr))';
    wrap.setAttribute('data-cse-cols', q.cols || 1);
    q.options.forEach(function (opt) {
      var selected = q.kind === 'multi' ? state.a.personal.indexOf(opt.v) !== -1 : state.a[q.id] === opt.v;
      var btn = document.createElement('button');
      btn.type = 'button';
      btn.className = 'cse-option' + (q.kind === 'multi' ? ' cse-option--multi' : '') + (selected ? ' is-selected' : '');
      btn.innerHTML = '<span class="cse-option-dot"></span>' +
        '<span><span class="cse-option-label">' + escapeHtml(opt.l) + '</span>' +
        (opt.d ? '<span class="cse-option-desc">' + escapeHtml(opt.d) + '</span>' : '') + '</span>';
      btn.addEventListener('click', function () {
        if (q.kind === 'multi') toggleMulti(opt.v); else pick(q.id, opt.v);
      });
      wrap.appendChild(btn);
    });
  }

  function escapeHtml(s) {
    return String(s).replace(/[&<>"']/g, function (c) {
      return { '&': '&amp;', '<': '&lt;', '>': '&gt;', '"': '&quot;', "'": '&#39;' }[c];
    });
  }
  function escapeAttr(s) { return escapeHtml(s); }

  function pick(id, v) {
    trackQuizStart();
    state.a[id] = v;
    renderCurrentStep();
    if (config.autoAdvance !== false) setTimeout(next, 180);
  }
  function toggleMulti(v) {
    trackQuizStart();
    var p = state.a.personal.slice();
    if (v === 'none') p = p.indexOf('none') !== -1 ? [] : ['none'];
    else {
      p = p.filter(function (x) { return x !== 'none'; });
      p = p.indexOf(v) !== -1 ? p.filter(function (x) { return x !== v; }) : p.concat(v);
    }
    state.a.personal = p;
    renderCurrentStep();
  }

  function renderCurrentStep() {
    var q = QUESTIONS[state.step];
    stepPanels.forEach(function (panel) {
      panel.hidden = Number(panel.getAttribute('data-cse-step-panel')) !== state.step;
    });
    var panel = stepPanels[state.step];
    if (q.options && panel) renderOptions(q, panel);
    if (progressBar) progressBar.style.width = (state.step / QUESTIONS.length * 100) + '%';
    if (stepLabel) stepLabel.textContent = (state.step + 1) + ' of ' + QUESTIONS.length;
    if (kindLabel) kindLabel.textContent = q.kind === 'multi' ? 'Select all that apply' : q.kind === 'identity' ? 'Your details' : 'Pick one';
    if (quizTitle) quizTitle.textContent = q.title;
    if (quizSub) quizSub.textContent = q.sub;
    if (backBtn) backBtn.hidden = state.step === 0;
    refreshFooter();
  }

  function refreshFooter() {
    var q = QUESTIONS[state.step];
    var ok = answered(q);
    /* Single-pick questions auto-advance the instant an option is clicked
       (see pick()), so the grey Continue button never does anything on
       them while auto-advance is on — it just sits there looking broken.
       Multi-select and identity always need an explicit Continue since
       there's no single click that means "done". */
    var hideContinue = q.kind === 'single' && config.autoAdvance !== false;
    if (nextBtn) {
      nextBtn.hidden = hideContinue;
      nextBtn.disabled = !ok;
      nextBtn.textContent = state.step === QUESTIONS.length - 1 ? 'Build my shop preview' : 'Continue';
    }
    if (helperText) helperText.textContent = (ok || hideContinue) ? '' : 'Answer to continue';
  }

  function next() {
    if (state.step >= QUESTIONS.length - 1) { showResult(); return; }
    state.step += 1;
    renderCurrentStep();
    scrollToQuiz();
  }
  function back() {
    state.step = Math.max(0, state.step - 1);
    renderCurrentStep();
  }
  if (nextBtn) nextBtn.addEventListener('click', next);
  if (backBtn) backBtn.addEventListener('click', back);

  var restartBtns = $$('[data-cse-restart]', root);
  restartBtns.forEach(function (b) { b.addEventListener('click', restart); });
  function restart() {
    state.step = 0; state.done = false;
    state.a = { audience: null, org: '', logo: '', logoUrl: '', logoUploadFailed: false, purpose: '', headcount: '', primary: '#DB124B', secondary: '#002E65', access: null, items: null, personal: [], runmode: null, delivery: null };
    logoUploadPromise = null;
    if (orgInput) orgInput.value = '';
    if (purposeSelect) purposeSelect.value = '';
    if (headcountSelect) headcountSelect.value = '';
    if (logoInput) logoInput.value = '';
    renderLogoPreview();
    renderIdentitySwatches();
    quizPanel.hidden = false;
    resultPanel.hidden = true;
    if (sentCard) {
      sentCard.hidden = true;
      $$('.cse-quiz-talk-link', root).forEach(function (el) { el.hidden = false; });
      var submitBtn = enquiryForm && $('button[type="submit"]', enquiryForm);
      if (submitBtn) { submitBtn.disabled = false; submitBtn.textContent = 'Send my shop enquiry'; }
      if (enquiryForm) enquiryForm.reset();
    }
    renderCurrentStep();
    scrollToQuiz();
  }

  function scrollToQuiz() {
    var target = $('[data-cse-scroll-target]', root) || root;
    var y = target.getBoundingClientRect().top + window.scrollY - 16;
    window.scrollTo({ top: y, behavior: 'smooth' });
  }

  /* ===================== Result / preview ===================== */
  function showResult() {
    state.done = true;
    quizPanel.hidden = true;
    resultPanel.hidden = false;
    renderResult();
    scrollToQuiz();
    trackEvent('club_shop_quiz_completed', { shop_type: shopType() });
  }

  function setText(sel, text) { var el = $(sel, root); if (el) el.textContent = text; }
  function setAll(sel, text) { $$(sel, root).forEach(function (el) { el.textContent = text; }); }

  function renderResult() {
    var a = state.a, rec = recommendation(), t = shopType();
    var club = a.org.trim() || 'Your Club';
    var initials = initialsOf(a.org);
    var slug = slugify(a.org);
    var aud = AUD[a.audience] || AUD_DEFAULT;

    setText('[data-cse-rec-name]', rec.name);
    setText('[data-cse-rec-tagline]', rec.tagline);
    setText('[data-cse-rec-headline]', rec.headline);
    setText('[data-cse-rec-summary]', rec.summary);
    setText('[data-cse-rec-timeline]', rec.timeline);

    var customNoteEl = $('[data-cse-custom-note]', root);
    if (customNoteEl) {
      customNoteEl.hidden = a.items !== 'custom';
      customNoteEl.textContent = "Ordering match kit in bulk as well? Mention it in the notes and we'll handle both.";
    }

    var reasonsWrap = $('[data-cse-reasons]', root);
    if (reasonsWrap) {
      reasonsWrap.innerHTML = '';
      reasons().forEach(function (r) {
        var div = document.createElement('div');
        div.className = 'cse-reason';
        div.innerHTML = '<span class="cse-reason-n">' + r.n + '</span><span class="cse-reason-text">' + escapeHtml(r.text) + '</span>';
        reasonsWrap.appendChild(div);
      });
    }

    setAll('[data-cse-club-name]', club);
    setText('[data-cse-shop-url]', 'kpisports.com.au/pages/' + slug + '-club-shop');
    setText('[data-cse-preview-title]', 'Preview: ' + club + ' club shop');

    var primaryFg = readable(a.primary);
    var secondaryFg = readable(a.secondary);

    /* Hero band mirrors club-shop-v2-6's .cs-hero: a gradient in the club's
       own primary colour, with a light crest disc (primary-coloured
       initials) sitting on top — never the same colour as the band itself. */
    $$('[data-cse-hero]', root).forEach(function (el) {
      el.style.background = 'linear-gradient(115deg, ' + shade(a.primary, -0.35) + ', ' + a.primary + ' 65%)';
      el.style.color = readable(a.primary);
    });
    $$('[data-cse-crest]', root).forEach(function (el) {
      if (a.logo) { el.hidden = true; }
      else { el.hidden = false; el.textContent = initials; el.style.background = '#fff'; el.style.color = a.primary; }
    });
    $$('[data-cse-logo-img]', root).forEach(function (el) {
      if (a.logo) { el.hidden = false; el.style.backgroundImage = 'url(' + a.logo + ')'; }
      else { el.hidden = true; }
    });
    $$('[data-cse-req-pill]', root).forEach(function (el) {
      el.style.background = a.primary;
      el.style.color = primaryFg;
    });

    $$('[data-cse-banner]', root).forEach(function (el) {
      el.style.background = a.secondary;
      el.style.color = secondaryFg;
      el.textContent = el.hasAttribute('data-cse-banner-short')
        ? (t === 'retail' ? 'In stock — ships now' : t === 'preorder' ? 'Pre-order — ordering window open' : 'Merch in stock // ' + aud.noun + ' pre-order')
        : (t === 'retail' ? 'In stock now — dispatched in 2–3 business days' : t === 'preorder' ? 'Pre-order ordering window open now — production starts once it closes' : 'Merch ships now // ' + aud.noun + ' ordering window open now');
    });

    var roleWraps = $$('[data-cse-role-strip]', root);
    roleWraps.forEach(function (el) { el.hidden = a.access !== 'roles'; });
    var firstChip = aud.chips[0].toLowerCase();
    setAll('[data-cse-first-chip]', firstChip);
    var roleChipsWrap = $('[data-cse-role-chips]', root);
    if (roleChipsWrap) {
      roleChipsWrap.innerHTML = '';
      aud.chips.forEach(function (label, i) {
        var span = document.createElement('span');
        span.className = 'cse-mock-role-chip';
        span.textContent = label;
        if (i === 0) { span.style.background = a.primary; span.style.color = primaryFg; span.style.borderColor = a.primary; }
        roleChipsWrap.appendChild(span);
      });
    }

    var products = previewProducts();
    var required = products.filter(function (p) { return p.group === 'req'; });
    var optional = products.filter(function (p) { return p.group === 'opt'; });
    fillProductGrid('[data-cse-required-desktop]', required, false, a);
    fillProductGrid('[data-cse-optional-desktop]', optional, false, a);
    fillProductGrid('[data-cse-required-phone]', required.slice(0, 4), true, a);

    setText('[data-cse-required-copy]', a.access === 'roles' ? aud.req : 'The core kit everyone needs.');

    var personalOpts = (QUESTIONS.filter(function (q) { return q.id === 'personal'; })[0] || {}).options || [];
    var personalList = a.personal.filter(function (x) { return x !== 'none'; }).map(function (v) {
      var o = personalOpts.filter(function (o) { return o.v === v; })[0];
      return o ? o.l : v;
    });
    var specs = [
      { k: 'Shop format', v: rec.name },
      { k: 'Product range', v: a.items === 'custom' ? 'Custom manufactured' : a.items === 'both' ? 'Custom + stock' : 'Stock with your branding' },
      { k: 'Ordering model', v: t === 'retail' ? 'Always open, ships on order' : t === 'preorder' ? 'Fixed ordering window, then production' : 'Open retail + scheduled pre-order windows' },
      { k: 'Who sees what', v: a.access === 'roles' ? 'Role, team or class filtered' : 'Open to everyone' },
      { k: 'Personalisation', v: personalList.length ? personalList.join(', ') : 'None' }
    ];
    var specsWrap = $('[data-cse-specs]', root);
    if (specsWrap) {
      specsWrap.innerHTML = '';
      specs.forEach(function (s) {
        var div = document.createElement('div');
        div.innerHTML = '<div class="cse-spec-k">' + escapeHtml(s.k) + '</div><div class="cse-spec-v">' + escapeHtml(s.v) + '</div>';
        specsWrap.appendChild(div);
      });
    }

    buildEnquiryBody(rec);
  }

  function colorOf(key, a) {
    return key === 'p' ? a.primary : key === 's' ? a.secondary : '#1d252d';
  }

  function fillProductGrid(sel, items, isPhone, a) {
    var wrap = $(sel, root);
    if (!wrap) return;
    wrap.innerHTML = '';
    var fg = readable(a.primary);
    items.forEach(function (p) {
      var card = document.createElement('div');
      card.className = isPhone ? 'cse-mock-phone-product' : 'cse-mock-product';
      var img = document.createElement('div');
      img.className = isPhone ? 'cse-mock-phone-product-img' : 'cse-mock-product-img';
      var tintColor = colorOf(p.colorKey, a);
      if (p.image) {
        /* A real stock photo of the garment. It isn't recoloured to the
           club's chosen colour — that would need real per-garment masks to
           look right — so a colour accent strip along the bottom does the
           "this prints in your colours" job instead. */
        img.style.backgroundImage = 'url(' + p.image + ')';
        img.style.backgroundSize = 'cover';
        img.style.backgroundPosition = 'center';
        var accent = document.createElement('span');
        accent.className = isPhone ? 'cse-mock-phone-product-accent' : 'cse-mock-product-accent';
        accent.style.background = tintColor;
        img.appendChild(accent);
      } else {
        img.style.background = 'linear-gradient(160deg, ' + tintColor + ' 0%, ' + tintColor + ' 58%, rgba(0,0,0,0.16) 58%, rgba(0,0,0,0.16) 100%)';
      }
      var tag = document.createElement('span');
      tag.className = 'cse-mock-tag';
      tag.style.background = p.tag === 'PRE-ORDER' ? a.primary : '#1d252d';
      tag.style.color = p.tag === 'PRE-ORDER' ? fg : '#fff';
      tag.textContent = p.tag;
      img.appendChild(tag);
      card.appendChild(img);
      var body = document.createElement('div');
      body.className = isPhone ? 'cse-mock-phone-product-body' : 'cse-mock-product-body';
      var html = '<div class="' + (isPhone ? 'cse-mock-phone-product-name' : 'cse-mock-product-name') + '">' + escapeHtml(p.title) + '</div>';
      if (isPhone) {
        html += '<div class="cse-mock-phone-product-price">' + escapeHtml(p.price) + '</div>';
        html += '<div class="cse-mock-phone-cta" style="background:' + a.primary + ';color:' + fg + '">Add to cart</div>';
      } else {
        html += '<div class="cse-mock-product-price-row"><span class="cse-mock-product-price">' + escapeHtml(p.price) + '</span><span class="cse-mock-size-note">' + (p.personalised ? 'Customisable' : 'Select size') + '</span></div>';
        html += '<div class="cse-mock-size-chips">' + ['XS', 'S', 'M', 'L', 'XL', '2XL'].map(function (s, i) {
          return '<span class="cse-mock-size-chip"' + (i === 2 ? ' style="border-color:' + a.primary + ';background:' + a.primary + ';color:' + fg + '"' : '') + '>' + s + '</span>';
        }).join('') + '</div>';
        if (p.personalised) html += '<div class="cse-mock-personal-note">' + escapeHtml(p.personalLabel) + '</div>';
        html += '<div class="cse-mock-add-cart" style="background:' + a.primary + ';color:' + fg + '">Add to cart</div>';
      }
      body.innerHTML = html;
      card.appendChild(body);
      wrap.appendChild(card);
    });
  }

  /* ===================== Enquiry / contact form ===================== */
  var bodyField = $('[data-cse-body-field]', root);
  var enquiryForm = $('.cse-enquiry-form', root);
  var sentCard = $('[data-cse-sent]', root);

  function showSent() {
    if (!sentCard) return;
    quizPanel.hidden = true;
    resultPanel.hidden = true;
    $$('.cse-quiz-talk-link', root).forEach(function (el) { el.hidden = true; });
    sentCard.hidden = false;
    scrollToQuiz();
    sentCard.focus({ preventScroll: true });
  }

  function answerLabel(qid) {
    var q = QUESTIONS.filter(function (qq) { return qq.id === qid; })[0];
    var o = (q && q.options || []).filter(function (o) { return o.v === state.a[qid]; })[0];
    return o ? o.l : '-';
  }

  function personalLabelsJoined() {
    var a = state.a;
    if (a.personal.indexOf('none') !== -1) return 'No personalisation needed';
    var pOpts = (QUESTIONS.filter(function (q) { return q.id === 'personal'; })[0] || {}).options || [];
    var labels = a.personal.map(function (v) {
      var o = pOpts.filter(function (o) { return o.v === v; })[0];
      return o ? o.l : v;
    });
    return labels.length ? labels.join(', ') : '-';
  }

  function bold(s) { return '<strong>' + escapeHtml(s || '-') + '</strong>'; }

  function summaryText(rec) {
    var a = state.a;
    var logoLine = a.logoUrl ? a.logoUrl
      : a.logoUploadFailed ? 'Uploaded, but the link failed to generate — email the club to ask them to send the logo file directly'
      : a.logo ? 'Uploaded — link still finalising, ask for it to be resent if missing below'
      : 'Not provided';
    var lines = [
      "Who's the shop for?: " + bold(answerLabel('audience')),
      'Club, school or business name: ' + bold(a.org),
      'Purpose of the shop: ' + bold(a.purpose),
      'Roughly how many people will order?: ' + bold(a.headcount),
      'Club logo: ' + logoLine,
      'Primary colour: ' + bold(colourLabel(a.primary)),
      'Secondary colour: ' + bold(colourLabel(a.secondary)),
      'Who gets to see the shop?: ' + bold(answerLabel('access')),
      'Custom-made or off the shelf?: ' + bold(answerLabel('items')),
      'Are you looking to personalise items?: ' + bold(personalLabelsJoined()),
      'How should the shop run?: ' + bold(answerLabel('runmode')),
      'How should the gear get to everyone?: ' + bold(answerLabel('delivery'))
    ];
    return 'CLUB SHOP ENQUIRY QUIZ\n\nRecommended setup: ' + rec.name + '\n' + rec.tagline +
      '\n\n--- QUIZ ANSWERS ---\n' + lines.join('\n') + '\n';
  }

  function buildEnquiryBody(rec) {
    if (!bodyField) return;
    bodyField.value = summaryText(rec);
  }

  if (enquiryForm) {
    enquiryForm.addEventListener('submit', function (e) {
      e.preventDefault();
      var submitBtn = $('button[type="submit"]', enquiryForm);
      if (submitBtn) { submitBtn.disabled = true; submitBtn.textContent = 'Sending…'; }
      ensureLogoUrl().then(function () {
        buildEnquiryBody(recommendation());
        trackEvent('club_shop_enquiry_sent', { shop_type: shopType() });
        if (!sentCard || !window.fetch) { enquiryForm.submit(); return; }
        /* Send in the background and confirm in place. Shopify redirects a
           successful contact post to ?contact_posted=true; anything else
           (validation errors, a spam challenge) falls back to a normal
           submit so the visitor sees Shopify's own response and nothing is lost. */
        fetch(enquiryForm.action, { method: 'POST', body: new FormData(enquiryForm), credentials: 'same-origin' })
          .then(function (res) {
            if (res.ok && /[?&]contact_posted=true/.test(res.url)) showSent();
            else enquiryForm.submit();
          })
          .catch(function () { enquiryForm.submit(); });
      });
    });
  }

  /* ===================== Scroll-to helpers (hero CTAs) =====================
     Anchors are looked up page-wide: on the sectioned template the targets
     live in sibling sections outside the quiz root. */
  $$('[data-cse-goto]', document).forEach(function (btn) {
    btn.addEventListener('click', function (e) {
      var name = btn.getAttribute('data-cse-goto');
      var target = $('[data-cse-anchor="' + name + '"]');
      if (!target) return;
      e.preventDefault();
      var y = target.getBoundingClientRect().top + window.scrollY - 16;
      window.scrollTo({ top: y, behavior: 'smooth' });
    });
  });

  /* ===================== Sticky mobile "Build my shop" bar ===================== */
  var stickyBar = $('[data-cse-sticky-bar]');
  var quizAnchorEl = $('#quiz') || $('[data-cse-anchor="quiz"]');
  if (stickyBar && quizAnchorEl && 'IntersectionObserver' in window) {
    var quizInView = new IntersectionObserver(function (entries) {
      stickyBar.classList.toggle('is-hidden', entries[0].isIntersecting);
    }, { threshold: 0 });
    quizInView.observe(quizAnchorEl);
  }

  /* A full-page submit (the fallback above) lands back here with
     ?contact_posted=true: go straight to the confirmation, not the quiz. */
  if (/[?&]contact_posted=true/.test(window.location.search)) {
    setTimeout(showSent, 0);
  }

  /* ===================== Deep link to #quiz ===================== */
  if (window.location.hash === '#quiz') {
    setTimeout(scrollToQuiz, 0);
  }

  /* ===================== FAQ: collapsed on mobile, open on desktop =====================
     Native <details> hides closed content in a way that isn't reliably
     overridable with CSS alone across browsers, so the open/closed state
     itself is set here instead — collapsed by default (mobile), forced
     open above the 641px breakpoint (desktop). */
  var faqItems = $$('.cse-faq-item', document);
  if (faqItems.length) {
    var applyFaqState = function () {
      var isDesktop = window.innerWidth >= 641;
      faqItems.forEach(function (d) { d.open = isDesktop; });
    };
    applyFaqState();
    window.addEventListener('resize', applyFaqState);
  }

  /* ===================== Init ===================== */
  renderLogoPreview();
  renderIdentitySwatches();
  renderCurrentStep();
})();
